import { useLayoutEffect, useState, Suspense, type ComponentType } from 'react';
import { Alert, Button, Loader, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  InterviewAccessPurpose,
  InterviewRecoveryReason,
  type InterviewSessionBootstrap,
  CallStatus,
} from 'api/generated/types';
import {
  WebCallDeletedSection,
  WebCallExpiredSection,
} from 'pages/web-call/components/LazyWebCallRecoverySections';
// eslint-disable-next-line no-restricted-imports -- Bootstrap replaces launch secrets before a candidate session is active; use the router directly without session propagation.
import { useNavigate } from 'react-router-dom';

import {
  CandidateSessionStorageError,
  type CandidateRouteParams,
  clearCandidateRouteParams,
  completeCandidateLaunch,
  deriveCandidateCredential,
  ensureCandidateSession,
  forgetInvitationSession,
  markRecoveryRequestPending,
  setCandidateRouteParams,
} from './candidateCapability';
import interviewApp from '../InterviewApp.module.css';

const RouteLoader = ({ label }: { label: string }) => (
  <div className={interviewApp.routeLoader}>
    <Loader aria-label={label} />
  </div>
);

/** Establish the route's interview session before any child can issue a request. */
export const RouteWrapper = ({
  component: Component,
}: {
  component: ComponentType;
}) => {
  const {
    encodedId: pathEncodedId,
    encodedTestData,
    encodedPracticeData,
    interviewId,
  } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const encodedId = pathEncodedId ?? searchParams.get('encodedId') ?? undefined;
  const interviewSession = searchParams.get('interviewSession') ?? undefined;
  const launch =
    new URLSearchParams(location.hash.slice(1)).get('launch') ?? undefined;
  // Older confirmation emails contain only the schedule ID, which is not a credential.
  const missingRescheduleCredential =
    location.pathname.startsWith('/reschedule/') &&
    !encodedId &&
    !interviewSession &&
    !launch;
  const [retry, setRetry] = useState(0);
  const credentialIdentity = launch
    ? `launch:${launch}`
    : interviewSession
      ? `session:${interviewSession}`
      : encodedId
        ? `invitation:${encodedId}`
        : `public:${location.pathname}`;
  const routeKey = `${credentialIdentity}:${retry}`;
  const [readyRoute, setReadyRoute] = useState<string | null>(null);
  const [componentIdentity, setComponentIdentity] = useState<string | null>(
    null
  );
  const [failedRoute, setFailedRoute] = useState<{
    key: string;
    storageUnavailable: boolean;
  } | null>(null);
  const [recovery, setRecovery] = useState<InterviewSessionBootstrap | null>(
    null
  );

  useLayoutEffect(() => {
    const params: CandidateRouteParams = {
      encodedId,
      interviewId,
      encodedTestData,
      encodedPracticeData,
      interviewSession,
      launch,
    };
    setCandidateRouteParams(params);
    let active = true;
    const context = deriveCandidateCredential(undefined, params);
    if (!context) {
      setFailedRoute(null);
      setRecovery(null);
      setComponentIdentity(`public:${location.pathname}`);
      setReadyRoute(routeKey);
    } else {
      void ensureCandidateSession(context).then(
        (session) => {
          if (!active) return;
          setFailedRoute(null);
          setRecovery(
            session.purpose === InterviewAccessPurpose.recovery
              ? session.bootstrap
              : null
          );
          if (launch) {
            // Only the one-time launch transition requires durable credentials
            // before replacing its URL authority with a nonauthorizing SID.
            const query = new URLSearchParams(location.search);
            query.set('interviewSession', session.session_id);
            const fragment = new URLSearchParams(location.hash.slice(1));
            fragment.delete('launch');
            navigate(
              {
                pathname: location.pathname,
                search: query.toString(),
                hash: fragment.toString(),
              },
              { replace: true }
            );
            completeCandidateLaunch(launch);
          } else {
            setComponentIdentity(session.session_id);
            setReadyRoute(routeKey);
          }
        },
        (error: unknown) => {
          // Do not log request objects: exchange errors can contain launch secrets.
          if (active)
            setFailedRoute({
              key: routeKey,
              storageUnavailable: error instanceof CandidateSessionStorageError,
            });
        }
      );
    }
    return () => {
      active = false;
      clearCandidateRouteParams(params);
    };
  }, [
    encodedId,
    encodedTestData,
    encodedPracticeData,
    interviewId,
    interviewSession,
    launch,
    location.pathname,
    location.search,
    location.hash,
    navigate,
    routeKey,
  ]);

  return (
    <div className={interviewApp.routeShell}>
      {missingRescheduleCredential ? (
        <Alert role='alert'>
          {t('scheduling.rescheduleReason.errors.invitationRequired')}
        </Alert>
      ) : failedRoute?.key === routeKey ? (
        <Stack>
          <Alert color='red' role='alert'>
            {t(
              failedRoute.storageUnavailable
                ? 'candidateSession.storageUnavailable'
                : 'rejectionReview.genericError'
            )}
          </Alert>
          <Button
            onClick={() => {
              const context = deriveCandidateCredential(undefined, {
                encodedId,
                interviewSession,
                launch,
              });
              if (context?.kind === 'web_call_link')
                forgetInvitationSession(context);
              setRetry((value) => value + 1);
            }}
          >
            {t('scheduling.actions.retry')}
          </Button>
        </Stack>
      ) : readyRoute === routeKey ? (
        <Suspense
          key={componentIdentity}
          fallback={<RouteLoader label={t('webCall.form.loading')} />}
        >
          {recovery ? (
            recovery.recovery_reason ===
              InterviewRecoveryReason.application_removed &&
            !recovery.recovery_interview_completed ? (
              <WebCallDeletedSection
                candidateId={recovery.candidate_id ?? null}
                callId={recovery.call_id ?? null}
                encodedId={null}
                alternateActiveLinkEncodedId={null}
                requestPending={recovery.recovery_request_pending ?? false}
                recoverySession
                onRequestSubmitted={() => {
                  if (componentIdentity)
                    markRecoveryRequestPending(componentIdentity);
                }}
              />
            ) : (
              <WebCallExpiredSection
                candidateId={recovery.candidate_id ?? null}
                callId={recovery.call_id ?? null}
                isTestCallOrTestEmail={false}
                pastCompletionRate={null}
                callStatus={
                  recovery.recovery_interview_completed
                    ? CallStatus.COMPLETED
                    : recovery.recovery_request_pending
                      ? CallStatus.NEW_INTERVIEW_REQUESTED
                      : null
                }
                dispositionedCandidate={false}
                onRequestSubmitted={() => {
                  if (componentIdentity)
                    markRecoveryRequestPending(componentIdentity);
                }}
              />
            )
          ) : (
            <Component />
          )}
        </Suspense>
      ) : (
        <RouteLoader label={t('webCall.form.loading')} />
      )}
    </div>
  );
};
